import { computed, Injectable, signal } from '@angular/core';
import { Clinic } from '../models/clinic.model';

@Injectable({ providedIn: 'root' })
export class ClinicService {
  static readonly TRIAL_DAYS = 14;
  private readonly clinic_ = signal<Clinic | null>(null);
  private readonly role_ = signal<string>('');
  private readonly impersonating_ = signal(false);

  readonly clinic = this.clinic_.asReadonly();
  readonly impersonating = this.impersonating_.asReadonly();
  readonly brandColor = computed(() => this.clinic_()?.primaryColor ?? '#374151');
  readonly isOwner = computed(() => this.role_() === 'owner');

  readonly trialDaysRemaining = computed(() => {
    const c = this.clinic_();
    if (!c?.trialStartedAt || c.subscriptionStatus !== 'trial') return null;
    const elapsed = Math.floor((Date.now() - new Date(c.trialStartedAt).getTime()) / 86_400_000);
    return Math.max(0, ClinicService.TRIAL_DAYS - elapsed);
  });

  readonly isTrialExpired = computed(() => {
    const remaining = this.trialDaysRemaining();
    return remaining !== null && remaining <= 0;
  });

  readonly isOnTrial = computed(() => this.clinic_()?.subscriptionStatus === 'trial');
  readonly isSolo = computed(() => this.clinic_()?.type === 'solo');

  setClinic(clinic: Clinic): void {
    this.clinic_.set(clinic);
  }

  setRole(role: string): void {
    this.role_.set(role);
  }

  startImpersonation(clinic: Clinic, role: string): void {
    this.clinic_.set(clinic);
    this.role_.set(role);
    this.impersonating_.set(true);
    sessionStorage.setItem('impersonation', JSON.stringify({ clinic, role }));
  }

  stopImpersonation(): void {
    this.clinic_.set(null);
    this.role_.set('');
    this.impersonating_.set(false);
    sessionStorage.removeItem('impersonation');
  }

  restoreImpersonation(): boolean {
    const stored = sessionStorage.getItem('impersonation');
    if (!stored) return false;
    try {
      const { clinic, role } = JSON.parse(stored) as { clinic: Clinic; role: string };
      this.clinic_.set(clinic);
      this.role_.set(role);
      this.impersonating_.set(true);
      return true;
    } catch {
      sessionStorage.removeItem('impersonation');
      return false;
    }
  }

  clear(): void {
    this.clinic_.set(null);
    this.role_.set('');
    this.impersonating_.set(false);
    sessionStorage.removeItem('impersonation');
  }
}
